import { AxiosError } from 'axios';

type ValidationDetail = {
  loc?: Array<string | number>;
  msg?: string;
  type?: string;
};

type ApiErrorPayload = {
  detail?: string | ValidationDetail[];
  message?: string;
  error?: string;
  errors?: Array<{ message?: string }>;
};

const formatValidationDetail = (detail: ValidationDetail): string | null => {
  if (!detail.msg) {
    return null;
  }

  const path = detail.loc
    ?.filter((segment) => segment !== 'body')
    .map((segment) => String(segment))
    .join('.');

  return path ? `${path}: ${detail.msg}` : detail.msg;
};

export const extractApiErrorMessage = (
  error: unknown,
  fallback = 'An unexpected error occurred'
): string => {
  if (!(error instanceof AxiosError)) {
    if (error instanceof Error && error.message) {
      return error.message;
    }
    return fallback;
  }

  const data = error.response?.data as ApiErrorPayload | undefined;

  if (typeof data?.detail === 'string' && data.detail.trim()) {
    return data.detail;
  }

  if (Array.isArray(data?.detail) && data.detail.length > 0) {
    return formatValidationDetail(data.detail[0]) ?? fallback;
  }

  if (typeof data?.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data?.error === 'string' && data.error.trim()) {
    return data.error;
  }

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0]?.message || fallback;
  }

  if (error.code === 'ECONNABORTED') {
    return 'Request timeout. Please try again.';
  }

  if (error.message) {
    return error.message;
  }

  return fallback;
};

const QUICKBOOKS_REAUTH_PATTERNS = [
  'reconnect quickbooks',
  'reconnect netsuite',
  'reconnect xero',
  'reconnect ajera',
  'quickbooks authentication expired',
  'netsuite authentication expired',
  'ajera authentication expired',
  'xero authentication',
  'xero auth',
  'ajera authentication',
  'ajera auth',
  'authentication expired',
  'invalid refresh token',
  'invalid_grant',
  'token expired',
  'authenticationfailed',
];

export const shouldReconnectQuickBooks = (error: unknown): boolean => {
  const message = extractApiErrorMessage(error, '').toLowerCase();

  if (!message) {
    return false;
  }

  return QUICKBOOKS_REAUTH_PATTERNS.some((pattern) => message.includes(pattern));
};
